import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { INoteModel } from '@src/slide/domain/ui-model/element-modeles/Interface/note-model.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IMenuItem } from '@src/slide/application/service/menu-manager/menu-items/menu-items.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { isWeworkUser } from 'utils';
import { SWITCH_KEY } from '@src/slide/infrastructure/module/dependent/config/switch-system/contant';
import { AddonMode } from '@src/slide/domain/ui-model/element-modeles/property/addon/addon.interface';
import SwitchManager from '@src/slide/infrastructure/module/dependent/config/switch-system';
import _set from 'lodash/set';
import i18n from '@i18next/i18n';
import * as PopMenu from '@editor/src/js/constants/right-menu-data';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { IMasterLayoutEditService } from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { browser } from '@utils/agent/index';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IAddonHandler } from '@src/slide/application/service/addon/addon-handler.interface';
import { IGapModel } from '@src/slide/domain/ui-model/element-modeles/Interface/gap-model.interface';
import { ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import DocsConfig from '@tencent/docs-config';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { isImageConfigurableForShapeImageFill } from '@src/slide/domain/ui-model/element-modeles/property/fill/image-fill/is-image-configurable';
import { getAllImage } from '../menu-values/element-menu-values-getter';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { isPreview } from '@src/slide/infrastructure/module/dependent/runtime-environment/drive-env';
import { REPORT_LOCATION } from '@src/slide/application/service/ai-replace-slide/ai-replace-slide-service.interface';
import { getToggleStatusByType, ToggleType } from '@src/slide/infrastructure/module/dependent/config/toggle-config';

/**
 * 获取右键菜单
 * @param model 图形 uiModel
 */
// eslint-disable-next-line complexity
export function getRightMenuItems(model: IBaseModel | INoteModel): IMenuItem[] {
    const modelType = model.type;
    const readModeMenuItems = rightMenuItemsInReadMode(model);
    // 只读模式菜单
    if (readModeMenuItems !== null) {
        return readModeMenuItems;
    }
    // 左侧缩略图间隔条
    if ([UIModelType.GAP].includes(modelType)) {
        return getGapRightMenuItems(model as IGapModel);
    }
    // 左侧列表节标题
    if ([UIModelType.SECTION].includes(modelType)) {
        return PopMenu.SlideSectionRightMenuItems as IMenuItem[];
    }
    // 组合
    if ([UIModelType.GROUP].includes(modelType)) {
        return getGroupRightMenuItems(model as IGroupModel);
    }
    // 多选图形
    if ([UIModelType.MULTIPLY].includes(modelType)) {
        return getMultiRightMenuItems(model as IMultiplyModel);
    }
    // 图表
    if ([UIModelType.CHART].includes(modelType)) {
        if (!(model as IChartModel).isActualRenderChart) return PopMenu.ChartFallbackMenuItems;
        return PopMenu.ChartRightMenuItems;
    }
    // 连接线
    if ([UIModelType.CONNECTOR].includes(modelType)) {
        return PopMenu.ConnectorRightMenuItems;
    }
    // SmartArt
    if ([UIModelType.SMART_ART].includes(modelType)) {
        return PopMenu.SmartArtMenuItems;
    }
    // slide
    if ([UIModelType.SLIDE].includes(modelType)) {
        return getSlideRightMenuItems();
    }
    // 版式
    if ([UIModelType.MASTER, UIModelType.LAYOUT].includes(modelType)) {
        return PopMenu.LayoutRightMenuItems as IMenuItem[];
    }
    // 空白
    if ([UIModelType.EMPTY].includes(modelType)) {
        return getBlankRightMenuItems(model);
    }
    // 图片
    if ([UIModelType.IMAGE].includes(modelType)) {
        return getImageRightMenuItems(model as IPictureModel);
    }
    // 表格
    if ([UIModelType.TABLE].includes(modelType)) {
        return getTableRightMenuItems(model as ITableModel);
    }
    // 形状
    if ([UIModelType.SHAPE].includes(modelType)) {
        return getShapeRightMenuItems(model as IShapeModel);
    }
    // 参考线
    if ([UIModelType.GUIDES].includes(modelType)) {
        return getGuidesRightItems();
    }
    // 主题
    if ([UIModelType.THEME].includes(modelType)) {
        return getThemeRightItems();
    }
    return [];
}

/**
 * 获取右键菜单(在只读模式)
 * @param model 图形 uiModel
 */
// eslint-disable-next-line complexity
function rightMenuItemsInReadMode(model: IBaseModel | INoteModel): IMenuItem[] | null {
    const userPriviledgeService = getUserPriviledgeService();
    if (userPriviledgeService.getHistoricalVersionStatus()) {
        return [];
    }
    // 选中的页存在未加载完成的，禁掉右键菜单
    if (isSlideModelPreload()) {
        return [];
    }
    // 编辑态不走后续逻辑
    const { pptEditorState } = userPriviledgeService;
    const isSlideOrGap = [UIModelType.SLIDE, UIModelType.GAP].includes(model.type);
    if (isSlideOrGap || pptEditorState === undefined || pptEditorState === PPTEditorStateType.EDITABLE) {
        return null;
    }
    const isPreviewMode = isPreview();
    const selectedElements = getSelectionService()?.selectedElements?.selectedArray;
    // 没有选中元素，预览态时右键无菜单，否则右键仅展示批注
    if (!selectedElements) {
        return isPreviewMode ? [] : PopMenu.CommentRightMenuItems.slice(0, 1);
    }
    // 只选中一个图片，预览态时显示复制和下载，否则显示复制、批注和下载
    if (selectedElements.length === 1 && selectedElements[0].type === UIModelType.IMAGE) {
        return isPreviewMode
            ? [...PopMenu.OnlyCopyRightMenuItems, ...PopMenu.OnlyDownloadOriginalImage]
            : [...PopMenu.OnlyCopyRightMenuItems, { id: ActionType.DIVIDER }, ...PopMenu.CommentRightMenuItems,
                ...PopMenu.OnlyDownloadOriginalImage];
    }
    // 其余场景，预览态时显示复制，否则显示复制和批注
    return isPreviewMode
        ? PopMenu.OnlyCopyRightMenuItems
        : [...PopMenu.OnlyCopyRightMenuItems, ...PopMenu.CommentRightMenuItems.slice().reverse()];
}

function isSlideModelPreload() {
    const selectionService = getSelectionService();
    return selectionService?.selectedSlides.selectedArray.some(e => e.preload)
        || selectionService?.currentSlide?.preload;
}

function getGapRightMenuItems(model: IGapModel): IMenuItem[] {
    if (model.isFromMasterLayout) {
        return PopMenu.LayoutGapRightMenuItems as IMenuItem[];
    }
    if (model.gapIndex === -1) {
        return (PopMenu.SlideGapRightMenuItems as IMenuItem[]).filter(item => item.id !== ActionType.ADD_SECTION);
    }
    return PopMenu.SlideGapRightMenuItems as IMenuItem[];
}

function getSlideRightMenuItems(): IMenuItem[] {
    const selectionService = getSelectionService();
    const slides = selectionService.selectedSlides.selectedArray;
    const result = [...PopMenu.SlideRightMenuItems] as IMenuItem[];
    const showCancelHide = slides.every((slide: ISlideModel) => !slide.isShowInPlay);
    const hideItemIndex = result.findIndex(item => item.id === ActionType.HIDE_SLIDE);
    // 重置隐藏按钮
    if (showCancelHide) {
        if (hideItemIndex >= 0) {
            const cancelHideItem: IMenuItem = {
                id: ActionType.CANCEL_HIDE_SLIDE,
                title: i18n.t('取消隐藏页面'),
            };
            result.splice(hideItemIndex, 1, cancelHideItem);
        }
    }

    // 动态添加 AI 单页美化按钮
    if (slides.length === 1) {
        appendAIReplaceButton(result, ActionType.SHOW_RATIO, REPORT_LOCATION.slideListRight);
    }

    return result;
}

function filterGuideItem(menuItems) {
    return menuItems;
}

function filterHideSlideItems(menuItems) {
    let hideSlideIndex = -1;
    let cancelHideSlideIndex = -1;
    menuItems.forEach((item, index) => {
        if (item.id === ActionType.HIDE_SLIDE) {
            hideSlideIndex = index;
        } else if (item.id === ActionType.CANCEL_HIDE_SLIDE) {
            cancelHideSlideIndex = index;
        }
    });
    hideSlideIndex >= 0 && menuItems.splice(hideSlideIndex, 1);
    cancelHideSlideIndex >= 0 && menuItems.splice(cancelHideSlideIndex, 1);
    return menuItems;
}

function filterAddSectionItem(menuItems) {
    const addSectionIndex = menuItems.findIndex(item => item.id === ActionType.ADD_SECTION);
    addSectionIndex >= 0 && menuItems.splice(addSectionIndex, 1);
    return menuItems;
}

function updateRightMenuForAddon(type: string, extraAddonId?: string) {
    getRightMenuManager().promise()
        .then(async (rightMenuManager) => {
            if (!rightMenuManager?.lastConfig) return;
            const {
                modelType,
                data,
            } = rightMenuManager.lastConfig;
            const addonMenuItems = data.find(item => item.id === ActionType.ADDON);
            if (!addonMenuItems) return;
            const addonHandler = getAddonManager().addonHandler as IAddonHandler;
            const newAddonSceneLists = await addonHandler.getSceneAddonListForMenu(type, extraAddonId);
            addonMenuItems.children = newAddonSceneLists;
            const {
                modelType: newModelType,
                data: newData,
                disableIds,
                config,
                onMenuItemClickCallBack,
            } = rightMenuManager.lastConfig;
            if (newModelType !== modelType) {
                return;
            }
            rightMenuManager.showMenu(modelType, newData, disableIds, config, onMenuItemClickCallBack);
        });
}

/**
 * 获取空白右键菜单
 * @param model 图形 uiModel
 */
function getBlankRightMenuItems(model: IBaseModel): IMenuItem[] {
    if (getMasterLayoutEditService()?.isMasterEditType) {
        return filterGuideItem(PopMenu.MasterRightMenuItems as any[]);
    }
    const menuItems: any[] = PopMenu.BlankRightMenuItems;
    if (getAddonManager().addonHandler) {
        const {
            addonSceneItemsMap,
            formatAddonSceneList,
            addonScenes,
        } = getAddonManager().addonHandler as IAddonHandler;
        const addonMenuItems = menuItems.find(item => item.id === ActionType.ADDON);
        if (addonMenuItems) {
            addonMenuItems.children = formatAddonSceneList(addonSceneItemsMap.get(addonScenes.CURSOR));
            updateRightMenuForAddon(addonScenes.CURSOR);
        }
    }
    const result = [...filterAddSectionItem(filterHideSlideItems(filterGuideItem(menuItems)))];

    appendAIReplaceButton(result, ActionType.SLIDE_PLAY, REPORT_LOCATION.slideRight);

    return result;
}
/**
 * 获取音视频额外右键菜单项
 * @param isVideo 是否是视频(可支持裁剪)
 * @returns
 */
function getMediaRightMenuItems(isVideo: boolean): IMenuItem[] {
    // 去除最后一个分隔符
    const menuItems = PopMenu.CommonRightMenuItems.slice(0, PopMenu.CommonRightMenuItems.length - 1);
    let mediaMenuItems = PopMenu.MediaRightMenuItems as any[];
    // 非视频类资源排除裁剪和封面选项
    if (!isVideo) {
        const deleteItems = [ActionType.TAILOR_PIC, ActionType.SET_COVER];
        mediaMenuItems = mediaMenuItems.filter(item => !deleteItems.includes(item.id));
    }
    return menuItems.concat(mediaMenuItems);
}

/**
 * 获取图片右键菜单(在只读模式)
 * @param model 图形 uiModel
 */
function getImageRightMenuItems(model: IPictureModel): IMenuItem[] {
    const menuItems = PopMenu.PicRightMenuItems;
    // 插件
    addAddonItems(model, menuItems);
    // 音视频
    if (model.isMedia()) {
        return getMediaRightMenuItems(model.isVideo());
    }

    // 图片类附件
    if (model.isAttachment()) {
        return PopMenu.ImageAttachmentRightMenuItems;
    }

    // 设置链接右键菜单显示文字
    changeLinkTitle(menuItems, model);

    if (needHideOcrEntry(model)) {
        return menuItems.filter(e => e.id !== ActionType.OCR_PICTURE);
    }
    const desc = model.sdkData?.getDescription();
    if (desc?.sourceUrl) {
        return menuItems.concat({
            id: ActionType.TO_SOURCE_URL,
            title: i18n.t('查看图片来源'),
        });
    }
    return menuItems;
}

function changeLinkTitle(menuItems, model) {
    let linItem;
    menuItems.forEach((item) => {
        if (item.id === ActionType.LINK) {
            linItem = item;
        }
        if (item.id === ActionType.DOWNLOAD_PICTURE && ENV.IS_PC_LOCAL_EDIT) {
            item.title = i18n.t('另存为图片');
        }
    });
    if (linItem) {
        linItem.title = i18n.t('链接');
        if (!model.editorInstance || (model.editorInstance && !model.editorInstance.isEditing)) {
            // 无焦点
            if (model.props.link) { // 存在对象链接
                linItem.title = i18n.t('编辑链接');
            }
        } else {
            // 有焦点，去拿文本框
            if (model.editorInstance?.getTextLinkState()) {
                linItem.title = i18n.t('编辑链接');
            }
        }
    }
}

function addAddonItems(model: IPictureModel, menuItems: IMenuItem[]): void {
    if (getAddonManager().addonHandler) {
        const {
            addonSceneItemsMap,
            formatAddonSceneList,
            addonScenes,
        } = getAddonManager().addonHandler as IAddonHandler;
        const addonMenuItems = menuItems.find(item => item.id === ActionType.ADDON);
        if (!addonMenuItems) {
            return;
        }
        addonMenuItems.children = formatAddonSceneList(addonSceneItemsMap.get(addonScenes.IMAGE));
        updateRightMenuForAddon(addonScenes.IMAGE, model.props.addon?.docElement.addonId);
    }
}

export function needHideOcrEntry(model: IPictureModel): boolean {
    const enableOCR = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.OCR) ?? true;
    // 图片秒看功能导致图片的url是blob格式，B端用户屏蔽入口，公式图片屏蔽入口
    return (isWeworkUser() || !enableOCR
        || !SwitchManager.getKeyValueFromMap(SWITCH_KEY.SLIDE_OCR_ENTRY)
        || ENV.IS_PC_LOCAL_EDIT
        || (!!model.props.addon && model.props.addon.type === 'latex'));
}

/**
 * 获取表格右键菜单
 * @param model 图形 uiModel
 */
function getTableRightMenuItems(model: ITableModel): IMenuItem[] {
    const menuItems = PopMenu.TableRightMenuItems;
    menuItems.forEach((value) => {
        const item = value;
        if (item.id === ActionType.MERGE_CELL) {
            setMergeCellTitle(model, item);
        }
    });

    const editElement = model.getEditingElement();
    if (editElement) {
        const copyItems = menuItems.map(item => item);
        if (model.isEditing) {
            copyItems.splice(copyItems.length - 1, 0, {
                id: ActionType.PARAGRAPH_MORE,
                title: i18n.t('段落'),
            } as any);
        }
        const title = editElement.getTextLinkState() ? i18n.t('编辑链接') : i18n.t('插入链接');
        copyItems.splice(copyItems.length - 1, 0, {
            id: ActionType.LINK,
            title,
            shortcut: 'Cmd+K',
        });
        return copyItems;
    }
    return menuItems;
}

function setMergeCellTitle(model: ITableModel, item) {
    const title = model.selectOneMergedCell() ? i18n.t('取消合并单元格') : i18n.t('合并单元格');
    _set(item, 'title', title);
}

/**
 * 获取参考线菜单
 */
function getGuidesRightItems(): IMenuItem[] {
    return PopMenu.GuidesSettingMenuItems;
}

/**
 * 获取主题菜单
 */
function getThemeRightItems(): IMenuItem[] {
    return PopMenu.ThemeSettingMenuItems;
}

/**
 * 获取形状右键菜单
 * @param model 图形 uiModel
 */
function getShapeRightMenuItems(model: IShapeModel): IMenuItem[] {
    const isIframe = model.props?.addon?.displayMode === AddonMode.IFRAME;
    if (isIframe) {
        return getIframeRightMenu(model);
    }
    if (model.isAttachment()) {
        return PopMenu.ShapeAttachmentRightMenuItems;
    }

    let menuItems;
    const isPlaceholderFromMasterOrLayout = model.isPlaceholder && (model.isFromLayout || model.isFromMaster);
    if (isPlaceholderFromMasterOrLayout) {
        menuItems = PopMenu.MasterAndLayoutPlaceHolderTextViewRightMeuItems;
    } else if (model.editorInstance?.isEditing) {
        menuItems = PopMenu.TextViewEditRightMenuItems;
    } else {
        if (isImageConfigurableForShapeImageFill(model)) {
            menuItems = PopMenu.TextViewImageFillRightMenuItems;
            const desc = model.sdkData?.getDescription();
            if (desc?.sourceUrl) {
                menuItems = menuItems.concat({
                    id: ActionType.TO_SOURCE_URL,
                    title: i18n.t('查看图片来源'),
                });
            }
        } else {
            menuItems = PopMenu.TextViewRightMenuItems;
        }
    }

    setMenuItemsLink(model, menuItems);
    return menuItems;
}

function getGroupRightMenuItems(model: IGroupModel): IMenuItem[] {
    return getAllImage(model)?.length
        ? PopMenu.GroupIncludeImageRightMenuItems
        : PopMenu.GroupRightMenuItems;
}

function getMultiRightMenuItems(model: IMultiplyModel): IMenuItem[] {
    return getAllImage(model)?.length
        ? PopMenu.MultiIncludeImageRightMenuItems
        : PopMenu.MultiRightMenuItems;
}

function getIframeRightMenu(model: IShapeModel): IMenuItem[] {
    const menuItems = PopMenu.CommonRightMenuItems.concat(PopMenu.CommentRightMenuItems);
    const {
        addonSceneItemsMap,
        formatAddonSceneList,
        addonScenes,
        addonInfoMap,
    } = getAddonManager().addonHandler as IAddonHandler;
    const addonId = model.props.addon?.docElement?.addonId;
    const addon = addonInfoMap.get(addonId);
    if (addon?.isOfficial || !addon) {
        return menuItems.slice(0, menuItems.length - 1);
    }
    PopMenu.AddonItem.children = formatAddonSceneList(addonSceneItemsMap.get(addonScenes.IFRAME));
    updateRightMenuForAddon(addonScenes.IFRAME, model.props.addon?.docElement.addonId);
    return menuItems.concat(PopMenu.AddonItem);
}

function setMenuItemsLink(model: IShapeModel, menuItems): void {
    // 设置链接右键菜单显示文字
    let linItem;
    menuItems.forEach((item) => {
        if (item.id === ActionType.LINK) {
            linItem = item;
        }
    });
    if (linItem) {
        linItem.title = i18n.t('插入链接');
        if (!model.editorInstance || (model.editorInstance && !model.editorInstance.isEditing)) {
            // 无焦点
            if (model.props.link) { // 存在对象链接
                linItem.title = i18n.t('编辑链接');
            }
        } else {
            // 有焦点，去拿文本框
            if (model.editorInstance?.getTextLinkState()) {
                linItem.title = i18n.t('编辑链接');
            }
        }
    }
}

// 动态添加 AI 单页美化按钮
function appendAIReplaceButton(menuConfig: IMenuItem[], appendId: string, location: REPORT_LOCATION) {
    if (!getToggleStatusByType(ToggleType.AI_REPLACE_SLIDE)) {
        return;
    }
    if (getAIController()?.checkIsSupportAIEdit()) {
        const showRatioIndex = menuConfig.findIndex(item => item.id === appendId);
        if (showRatioIndex >= 0) {
            const replaceByAIItem: IMenuItem[] = [
                {
                    id: ActionType.DIVIDER,
                },
                {
                    id: ActionType.AI_REPLACE_SLIDE,
                    title: i18n.t('AI 单页美化'),
                },
            ];
            menuConfig.splice(showRatioIndex + 1, 0, ...replaceByAIItem);
            tdw({
                opername: TDWOperationType.DOC_PPT_AI,
                module: TDWModuleType.PAGE_BEAUTY,
                action: TDWActionType.VIEW,
                ver5: TDWModuleType.PC,
                ver6: location,
            });
        }
    }
}

function getSelectionService() {
    return (AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices)
        .viewableServiceLayer.selectionService;
}

function getAIController() {
    return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
        .editableServiceLayer?.aiController;
}

function getUserPriviledgeService(): IUserPriviledgeService {
    return (AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices)
        .viewableServiceLayer.userPriviledgeService;
}

function getMasterLayoutEditService(): IMasterLayoutEditService | undefined {
    if (browser.isMobile || !getUserPriviledgeService().isEditable()) {
        return undefined;
    }
    return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
        .editableServiceLayer?.masterLayoutEditService;
}

function getAddonManager() {
    return (AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices)
        .viewableServiceLayer.addonManager;
}

function getRightMenuManager() {
    return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
        .viewableServiceLayer.rightMenuManager;
}
